import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Popover } from "antd";
import { Link } from "react-router-dom";
import { Localize } from "components/service";


const StaffWorkerStatusPopover = ({ to, translationKey, translationParams, children }) => {
    const [ visible, setVisible ] = useState(false);
    const triggerRef = useRef(null);
    const contentRef = useRef(null);
    const viewRef = useRef(null);
    const contentID = useId();
    const descriptionID = `${ contentID }-description`;
    const contains = useCallback(target => !!target && (triggerRef.current?.contains(target) || contentRef.current?.contains(target)), []);

    useEffect(() => {
        if (!visible) return undefined;

        const dismissOutside = event => {
            if (!contains(event.target)) setVisible(false);
        };
        const dismissEscape = event => {
            if (event.key !== "Escape") return;
            if (contentRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
            setVisible(false);
        };
        document.addEventListener("keydown", dismissEscape);
        document.addEventListener("focusin", dismissOutside);
        document.addEventListener("mousedown", dismissOutside);
        document.addEventListener("touchstart", dismissOutside);
        return () => {
            document.removeEventListener("keydown", dismissEscape);
            document.removeEventListener("focusin", dismissOutside);
            document.removeEventListener("mousedown", dismissOutside);
            document.removeEventListener("touchstart", dismissOutside);
        };
    }, [ visible, contains ]);

    const handleBlur = event => {
        if (!contains(event.relatedTarget)) setVisible(false);
    };

    return (
        <Popover
            visible={ visible }
            trigger={ [ "hover" ] }
            placement="topLeft"
            overlayClassName="staff-worker-status-popover"
            destroyTooltipOnHide={ true }
            onVisibleChange={ nextVisible => {
                if (nextVisible || !contains(document.activeElement)) setVisible(nextVisible);
            } }
            content={
                <div ref={ contentRef } id={ contentID } role="group" aria-describedby={ descriptionID }>
                    <span id={ descriptionID }><Localize { ...translationParams }>{ translationKey }</Localize></span>
                    { " " }
                    <Link ref={ viewRef } to={ to }
                        className="staff-worker-status-popover__view"
                        aria-describedby={ descriptionID }
                        onBlur={ handleBlur }
                        onKeyDown={ event => {
                            if (event.key !== "Tab") return;
                            // Restore the origin before native Tab continues, so a body portal
                            // does not move keyboard navigation to the end of the document.
                            triggerRef.current?.focus();
                            if (event.shiftKey) event.preventDefault();
                            else setVisible(false);
                        } }>
                        <Localize>STAFF_MANAGEMENT.Action_View</Localize>
                    </Link>
                </div>
            }>
            { React.cloneElement(children, {
                ref: triggerRef,
                "aria-expanded": visible,
                "aria-controls": visible ? contentID : undefined,
                "aria-describedby": visible ? descriptionID : undefined,
                onFocus: () => setVisible(true),
                onBlur: handleBlur,
                onKeyDown: event => {
                    if (event.key === "Tab" && !event.shiftKey && visible && viewRef.current)
                    {
                        event.preventDefault();
                        viewRef.current.focus();
                    }
                },
            }) }
        </Popover>
    );
};

export default StaffWorkerStatusPopover;
